import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { TICKET_BUCKETS, type CapacityPoint, type TicketMixPoint } from "../lib/api/types";

const AXIS = { fill: "hsl(var(--muted-foreground))", fontSize: 11 };
const TOOLTIP = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "0px",
  color: "hsl(var(--popover-foreground))",
  fontSize: 12,
};

/**
 * Distinct colors for the 8 ticket buckets: chart-1..5 plus three derived shades.
 *
 * Note the hsl() wrapper on every token. --chart-N and --card are declared as BARE HSL components
 * ("33 98% 58%"), which Tailwind resolves but SVG does not: `fill="var(--chart-1)"` is an invalid
 * paint and silently falls back to black. That is what made this chart render as a solid black
 * rectangle. See lib/chartColors.ts before adding any new color here.
 */
export const BUCKET_COLORS: Record<string, string> = {
  "0-10K": "hsl(var(--chart-1))",
  "10-25K": "hsl(var(--chart-2))",
  "25-50K": "hsl(var(--chart-3))",
  "50-100K": "hsl(var(--chart-4))",
  "100-150K": "hsl(var(--chart-5))",
  "150-200K": "color-mix(in oklch, hsl(var(--chart-1)) 55%, hsl(var(--card)))",
  "200-250K": "color-mix(in oklch, hsl(var(--chart-2)) 55%, hsl(var(--card)))",
  "Over 250K": "color-mix(in oklch, hsl(var(--chart-3)) 55%, hsl(var(--card)))",
};

export function CapacityIndexChart({ series }: { series: CapacityPoint[] }) {
  /**
   * Headcount is null until athena_ue_performance.team_headcount has rows.
   *
   * This guard is load-bearing, not defensive tidiness. Left unguarded,
   * `Math.min(volumeIndex, null)` is 0 and `Math.max(0, volumeIndex - null)` is volumeIndex —
   * so a missing headcount does not blank the band, it paints a "capacity gap" spanning the
   * entire chart from zero. That is the single most flattering possible misreading of the data,
   * which is exactly why it must not be the failure mode.
   */
  const hasHeadcount = series.some((p) => p.headcountIndex != null);

  const data = series.map((p) => ({
    period: p.period,
    volumeIndex: p.volumeIndex,
    headcountIndex: p.headcountIndex,
    gapBase: hasHeadcount ? Math.min(p.volumeIndex, p.headcountIndex ?? p.volumeIndex) : 0,
    gap: hasHeadcount ? Math.max(0, p.volumeIndex - (p.headcountIndex ?? p.volumeIndex)) : 0,
  }));

  return (
    <div className="h-[320px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="period" tick={AXIS} stroke="hsl(var(--border))" interval={2} />
          <YAxis tick={AXIS} stroke="hsl(var(--border))" domain={[80, "auto"]} />
          <Tooltip contentStyle={TOOLTIP} />
          {hasHeadcount ? (
            <Area
              dataKey="gapBase"
              stackId="gap"
              stroke="none"
              fill="transparent"
              isAnimationActive={false}
              legendType="none"
              name="Baseline"
            />
          ) : null}
          {hasHeadcount ? (
            <Area
              dataKey="gap"
              stackId="gap"
              stroke="none"
              fill="var(--amber)"
              fillOpacity={0.16}
              isAnimationActive={false}
              legendType="none"
              name="Capacity gap"
            />
          ) : null}
          <Line
            dataKey="volumeIndex"
            name="Applications Decided (index)"
            stroke="var(--amber)"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          {hasHeadcount ? (
            <Line
              dataKey="headcountIndex"
              name="Active Headcount (index)"
              stroke="var(--neutral-inactive)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ) : null}
          <Legend wrapperStyle={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Whether a capacity series carries any headcount at all — drives the card's caveat line. */
export function hasHeadcountData(series: CapacityPoint[]): boolean {
  return series.some((p) => p.headcountIndex != null);
}

export function VolumeNormalizedChart({ series }: { series: CapacityPoint[] }) {
  return (
    <div className="h-[320px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={series} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="period" tick={AXIS} stroke="hsl(var(--border))" interval={2} />
          <YAxis yAxisId="left" tick={AXIS} stroke="hsl(var(--border))" />
          <YAxis yAxisId="right" orientation="right" tick={AXIS} stroke="hsl(var(--border))" />
          <Tooltip contentStyle={TOOLTIP} />
          <Bar
            yAxisId="left"
            dataKey="applicationsDecided"
            name="Applications decided"
            fill="var(--neutral-inactive)"
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
          />
          <Line
            yAxisId="right"
            dataKey="avgFirstDecisionTimeHours"
            name="Avg first decision time (hrs)"
            stroke="var(--amber)"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Legend wrapperStyle={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function TicketMixChart({ series }: { series: TicketMixPoint[] }) {
  const data = series.map((p) => ({ period: p.period, ...p.mixPct }));

  return (
    <div className="h-[320px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="period" tick={AXIS} stroke="hsl(var(--border))" interval={2} />
          <YAxis
            tick={AXIS}
            stroke="hsl(var(--border))"
            unit="%"
            domain={[0, 100]}
            tickFormatter={(v: number) => String(Math.round(v))}
          />
          <Tooltip contentStyle={TOOLTIP} />
          {TICKET_BUCKETS.map((bucket) => (
            <Area
              key={bucket}
              dataKey={bucket}
              name={bucket}
              stackId="mix"
              stroke={BUCKET_COLORS[bucket]}
              fill={BUCKET_COLORS[bucket]}
              fillOpacity={0.75}
              isAnimationActive={false}
            />
          ))}
          <Legend wrapperStyle={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function BucketDecisionTimeChart({ series }: { series: TicketMixPoint[] }) {
  const data = series.map((p) => ({ period: p.period, ...p.decisionTimeWithinBucket }));

  return (
    <div className="h-[280px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="period" tick={AXIS} stroke="hsl(var(--border))" interval={2} />
          <YAxis tick={AXIS} stroke="hsl(var(--border))" unit="h" />
          <Tooltip contentStyle={TOOLTIP} />
          {TICKET_BUCKETS.map((bucket) => (
            <Line
              key={bucket}
              dataKey={bucket}
              name={bucket}
              stroke={BUCKET_COLORS[bucket]}
              strokeWidth={1.75}
              dot={false}
              isAnimationActive={false}
            />
          ))}
          <Legend wrapperStyle={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
